import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Formik, Form, Field, useFormikContext } from "formik";
import { Modal, Form as BootstrapForm, Button, Row, Col, Spinner, Badge } from "react-bootstrap";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { fetchAccounts } from "../../redux/features/accounts.features";
import { addTransaction } from "../../redux/features/transactions.features";
import apiServices from "../../services/apiServices";
import {
  ArrowDownLeft, ArrowUpRight, CheckCircle2, AlertCircle,
  Info, FileText, ShieldCheck, X, Tag, User, Layers, ShoppingBag
} from "lucide-react";
import { toast } from "react-toastify";

// Helper component to handle side effects that require Formik context
const DuesFetcher = ({ supplierId, accountType, onFetch }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    if (accountType === "Supplier" && supplierId) {
      onFetch(supplierId, values.agent_id);
    }
  }, [values.agent_id, supplierId, accountType, onFetch]);

  return null;
};

const TransactionForm = ({ show, onHide, paidType = "Deposit", account_type = "Company", account_id, onSubmit }) => {
  const dispatch = useDispatch();
  const { accounts, loading: accountsLoading } = useSelector((state) => state.accountsReducer);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [supplierDetails, setSupplierDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [isSubmittingLocal, setIsSubmittingLocal] = useState(false);
  const [duePurchases, setDuePurchases] = useState([]);
  const [loadingDues, setLoadingDues] = useState(false);

  useEffect(() => {
    if (show) {
      dispatch(fetchAccounts());
    }
  }, [dispatch, show]);

  useEffect(() => {
    if (account_id && accounts.length) {
      const account = accounts.find((acc) => acc._id === account_id);
      setSelectedAccount(account);

      // If it's a supplier, fetch extra details for agents and brands
      if (account?.account_owner_type === "Supplier" && account.related_party_id) {
        fetchSupplierExtras(account.related_party_id);
      }
    }
  }, [account_id, accounts]);

  const fetchSupplierExtras = async (supplierId) => {
    setLoadingDetails(true);
    try {
      const res = await apiServices.get(`/suppliers/${supplierId}`);
      if (res.data) {
        setSupplierDetails(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch supplier details", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const fetchDuePurchases = useCallback(async (supplierId, agentId) => {
    setLoadingDues(true);
    try {
      const res = await apiServices.get(`/purchase/due/supplier/${supplierId}`, {
        params: { agent_id: agentId || "direct" }
      });
      setDuePurchases(res.data || []);
    } catch (err) {
      console.error("Failed to fetch due purchases", err);
      setDuePurchases([]);
    } finally {
      setLoadingDues(false);
    }
  }, []); // Empty deps because it only uses apiServices (stable) and setters

  const config = useMemo(() => {
    const base = {
      Deposit: { icon: ArrowDownLeft, color: '#059669', bg: '#ecfdf5', label: 'Credit Entry', type: 'Inward' },
      Withdrawal: { icon: ArrowUpRight, color: '#dc2626', bg: '#fef2f2', label: 'Debit Entry', type: 'Outward' }
    };

    const overrides = {
      Supplier: { Deposit: "Pay Due", Withdrawal: "Supplier Refund" },
      Customer: { Deposit: "Collection / Advance", Withdrawal: "Issue Refund" },
      Employee: { Deposit: "Salaries / Advance", Withdrawal: "Settlement" },
      Company: { Deposit: "Internal Credit", Withdrawal: "Transfer / Expense" }
    };

    const currentType = overrides[account_type] || overrides.Company;
    return {
      ...base[paidType] || base.Deposit,
      actionLabel: (currentType && currentType[paidType]) ? currentType[paidType] : (paidType === "Deposit" ? "Deposit" : "Withdrawal")
    };
  }, [account_type, paidType]);

  const validationSchema = Yup.object().shape({
    account_id: Yup.string().required("Target account is required"),
    transaction_type: Yup.string().required("Method is required"),
    amount: Yup.number()
      .required("Amount is required")
      .positive("Amount must be greater than zero")
      .test("limit-check", "Insufficient balance", function (value) {
        if (paidType === "Withdrawal" && account_type !== "Supplier" && selectedAccount) {
          return value <= selectedAccount.balance;
        }
        return true;
      }),
    reason: Yup.string().required("Reference/Reason is required").min(3, "Too short"),
  });

  const handleFormSubmit = async (values, { resetForm }) => {
    setIsSubmittingLocal(true);
    try {
      const res = await dispatch(addTransaction(values));
      if (res.error) {
        toast.error(res.error.message || "Transaction failed");
      } else {
        toast.success(`${config.actionLabel} processed successfully`);
        resetForm();
        if (onSubmit) onSubmit();
      }
    } catch (err) {
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmittingLocal(false);
    }
  };

  const Icon = config.icon;

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="md" className="transaction-modal-premium">
      <style>
        {`
                .transaction-modal-premium .modal-content {
                    border-radius: 24px;
                    border: none;
                    overflow: hidden;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }
                .transaction-modal-premium .modal-body {
                    padding: 0;
                }
                .premium-input {
                    width: 100%;
                    background: #f8fafc;
                    border: 2px solid #f1f5f9;
                    border-radius: 16px;
                    padding: 14px 14px 14px 44px;
                    font-size: 15px;
                    transition: all 0.2s;
                    outline: none;
                }
                .premium-input:focus {
                    border-color: #6366f1;
                    background: #fff;
                    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
                }
                .input-label {
                    display: block;
                    font-size: 11px;
                    font-weight: 800;
                    color: #94a3b8;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    margin-bottom: 8px;
                    margin-left: 4px;
                }
                .action-btn {
                    width: 100%;
                    background: #1e293b;
                    color: #fff;
                    border: none;
                    border-radius: 16px;
                    padding: 16px;
                    font-size: 13px;
                    font-weight: 800;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                    display: flex;
                    align-items: center;
                    justify-content: center; gap: 10px;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-btn:hover:not(:disabled) {
                    background: #0f172a;
                    transform: translateY(-2px);
                    box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                }
                .action-btn:disabled {
                    background: #e2e8f0;
                    color: #94a3b8;
                    cursor: not-allowed;
                }
                .close-btn-custom {
                    position: absolute;
                    top: 20px;
                    right: 20px;
                    background: rgba(0,0,0,0.05);
                    border: none;
                    border-radius: 50%;
                    width: 32px;
                    height: 32px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.2s;
                    z-index: 10;
                }
                .close-btn-custom:hover {
                    background: rgba(0,0,0,0.1);
                    color: #1e293b;
                }
                @keyframes modal-spin { to { transform: rotate(360deg); } }
                `}
      </style>

      <button className="close-btn-custom" onClick={onHide}>
        <X size={18} />
      </button>

      {accountsLoading && !selectedAccount ? (
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted fw-bold small uppercase">Accessing Ledger...</div>
        </div>
      ) : (
        <Formik
          initialValues={{
            account_id: account_id || "",
            transaction_type: paidType || "Deposit",
            amount: "",
            reason: "",
            agent_id: "",
            segment_id: ""
          }}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={handleFormSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => {
            const currentBalance = selectedAccount?.balance || 0;
            const isDeposit = values.transaction_type === "Deposit";
            const amountNum = Number(values.amount) || 0;
            const projectedBalance = isDeposit ? currentBalance + amountNum : currentBalance - amountNum;
            const agentDueSum = duePurchases.reduce((acc, p) => acc + (p.payment_due_amount || 0), 0);

            return (
              <Form>
                <DuesFetcher
                  supplierId={supplierDetails?._id}
                  accountType={account_type}
                  onFetch={fetchDuePurchases}
                />

                {/* Header */}
                <div style={{ background: '#f8fafc', padding: '28px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ background: config.bg, color: config.color, padding: '10px', borderRadius: '12px', display: 'flex' }}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <h3 style={{ fontSize: '14px', fontWeight: '900', color: '#1e293b', margin: 0, textTransform: 'uppercase' }}>{config.actionLabel}</h3>
                      <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0, fontWeight: '700', textTransform: 'uppercase' }}>{account_type} Ledger</p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', paddingRight: '40px' }}>
                    <p style={{ fontSize: '10px', color: '#94a3b8', fontWeight: '800', textTransform: 'uppercase', marginBottom: '2px' }}>Global Dues</p>
                    <p style={{ fontSize: '15px', fontWeight: '900', color: currentBalance < 0 ? '#dc2626' : '#1e293b', margin: 0 }}>Rs. {Math.abs(currentBalance).toLocaleString()}</p>
                  </div>
                </div>

                <div style={{ padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

                  {/* Agent & Brand Selection for Suppliers */}
                  {account_type === "Supplier" && supplierDetails && (
                    <div style={{ background: '#f8fafc', padding: '20px', borderRadius: '20px', border: '1px solid #f1f5f9' }}>
                      <label className="input-label mb-3">Treasury Target</label>
                      <Row className="g-3">
                        <Col md={12}>
                          <div className="position-relative">
                            <User size={16} className="position-absolute" style={{ left: '16px', top: '16px', color: '#94a3b8' }} />
                            <Field as="select" name="agent_id" className="premium-input" style={{ paddingLeft: '44px' }}>
                              <option value="">Direct Distribution (Company)</option>
                              {supplierDetails.contacts?.map((contact, i) => (
                                <option key={i} value={contact._id}>{contact.name} ({contact.designation || 'Agent'})</option>
                              ))}
                            </Field>
                          </div>
                        </Col>
                      </Row>

                      {/* Agent Specific Snapshot */}
                      <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <ShoppingBag size={14} className="text-slate-400" />
                          <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b' }}>Due Invoices: {duePurchases.length}</span>
                        </div>
                        <span style={{ fontSize: '13px', fontWeight: '900', color: agentDueSum > 0 ? '#dc2626' : '#059669' }}>
                          Rs. {agentDueSum.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Amount */}
                  <div>
                    <label className="input-label">Transaction Amount (LKR)</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontWeight: '800', fontSize: '14px' }}>Rs.</div>
                      <Field name="amount" type="number" placeholder="0.00" className="premium-input" style={{ fontSize: '18px', fontWeight: '900', border: errors.amount && touched.amount ? '2px solid #fecaca' : undefined }} />
                    </div>
                    {errors.amount && touched.amount && (
                      <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <AlertCircle size={10} /> {errors.amount}
                      </div>
                    )}
                  </div>

                  {/* Projection */}
                  {amountNum > 0 && (
                    <div style={{ background: isDeposit ? '#f0fdf4' : '#fff7ed', border: `1px solid ${isDeposit ? '#bcf0da' : '#ffedd5'}`, padding: '16px', borderRadius: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Info size={14} color={isDeposit ? '#10b981' : '#f97316'} />
                        <span style={{ fontSize: '10px', fontWeight: '800', color: '#64748b', textTransform: 'uppercase' }}>After {config.actionLabel}</span>
                      </div>
                      <span style={{ fontSize: '14px', fontWeight: '900', color: projectedBalance < 0 ? '#dc2626' : '#1e293b' }}>Rs. {Math.abs(projectedBalance).toLocaleString()}</span>
                    </div>
                  )}

                  {/* Reason */}
                  <div>
                    <label className="input-label">Internal Reference / Notes</label>
                    <div style={{ position: 'relative' }}>
                      <div style={{ position: 'absolute', left: '16px', top: '16px', color: '#94a3b8' }}><FileText size={16} /></div>
                      <Field name="reason" as="textarea" rows={2} placeholder="Provide a reference for this payment..." className="premium-input" style={{ resize: 'none' }} />
                    </div>
                    {errors.reason && touched.reason && (
                      <div style={{ color: '#ef4444', fontSize: '10px', fontWeight: '800', textTransform: 'uppercase', marginTop: '6px' }}>{errors.reason}</div>
                    )}
                  </div>

                  {/* Submit */}
                  <div style={{ paddingTop: '12px' }}>
                    <button type="submit" disabled={isSubmittingLocal} className="action-btn">
                      {isSubmittingLocal ? (
                        <>
                          <div style={{ width: '16px', height: '16px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'modal-spin 0.6s linear infinite' }} />
                          Syncing Ledger...
                        </>
                      ) : (
                        <>Confirm {config.actionLabel} <CheckCircle2 size={16} /></>
                      )}
                    </button>
                    <p style={{ fontSize: '9px', textAlign: 'center', color: '#94a3b8', fontWeight: '800', textTransform: 'uppercase', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                      <ShieldCheck size={12} /> Verified Financial Transaction
                    </p>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default TransactionForm;
